import {useEffect,useRef,useState} from 'react';
import {POLICY_PAGE_SIZE,parsePolicyLibrary,policyErrorText,policySourceUrl,policyStateText,type PolicyLibrary,type PolicyLoader,type PolicyYear,type OfficialSourceReference} from './policy';
import './policy.css';
import {OfficialSourceList} from './OfficialSourceList';
function date(value:string|null){if(!value)return '미기록';const d=new Date(value);return Number.isNaN(d.getTime())?'날짜 확인 필요':d.toLocaleDateString('ko-KR',{timeZone:'Asia/Seoul'});}
export function CenterPolicyLibrary({schoolId,load,onLegacy,requireYearChoice=false,initialYearId,hideYearPicker=false,onSelectSource}:{schoolId:string;load:PolicyLoader;onLegacy:()=>void;requireYearChoice?:boolean;initialYearId?:string;hideYearPicker?:boolean;onSelectSource?:(source:OfficialSourceReference)=>void}){
  const [year,setYear]=useState(initialYearId||'');const [offset,setOffset]=useState(0);const [refresh,setRefresh]=useState(0);
  const [years,setYears]=useState<{schoolId:string;items:PolicyYear[]}>({schoolId,items:[]});
  const [state,setState]=useState<{key:string;status:'loading'|'error'|'ready';data?:PolicyLibrary;error?:string}>({key:'',status:'loading'});
  const sequence=useRef(0);const key=`${schoolId}:${year}:${offset}:${refresh}`;
  useEffect(()=>{
    const id=++sequence.current;const controller=new AbortController();
    setState({key,status:'loading'});
    const timer=window.setTimeout(()=>{if(sequence.current===id&&!controller.signal.aborted){controller.abort();setState({key,status:'error',error:'자료 연결이 지연되고 있어요. 다시 시도해 주세요.'});}},15000);
    const request={schoolId,academicYearId:year||null,offset,signal:controller.signal};
    load(request).then(raw=>{
      if(controller.signal.aborted||sequence.current!==id)return;
      window.clearTimeout(timer);
      const data=parsePolicyLibrary(raw,request);
      setYears({schoolId,items:data.years});setState({key,status:'ready',data});
    }).catch(error=>{
      window.clearTimeout(timer);
      if(controller.signal.aborted||sequence.current!==id)return;
      setYears({schoolId,items:[]});setState({key,status:'error',error:policyErrorText(error)});
    });
    return ()=>{window.clearTimeout(timer);controller.abort();};
  },[key,load,schoolId,year,offset]);
  const current=state.key===key?state:null;
  const data=current?.status==='ready'&&(!requireYearChoice||year)?current.data:undefined;
  const availableYears=years.schoolId===schoolId?years.items:[];
  const loading=!current||current.status==='loading';
  return <section className="rs-calm-policy" aria-label="학교에 연결된 자료">
    <div className="rs-calm-policy-toolbar">
      {!hideYearPicker&&availableYears.length>0&&<label>학년도 <select aria-label="자료 학년도" value={year||data?.academicYearId||''} onChange={e=>{setYear(e.target.value);setOffset(0);}}>
        <option value="">학년도 선택</option>{availableYears.map(y=><option key={y.id} value={y.id}>{y.label}</option>)}
      </select></label>}
      <button className="rs-calm-link" onClick={()=>{setOffset(0);setRefresh(v=>v+1);}} disabled={loading}>다시 불러오기</button>
    </div>
    {loading&&<p role="status">등록된 자료를 가져오고 있어요.</p>}
    {current?.status==='error'&&<div role="alert"><p>{current.error}</p><button className="rs-calm-link" onClick={()=>setRefresh(v=>v+1)}>다시 시도</button></div>}
    {current?.status==='ready'&&requireYearChoice&&!year&&<p role="status">요청한 자료의 학년도를 선택해 주세요.</p>}
    {data&&data.state!=='READY'&&!(data.state==='PACK_REQUIRED'&&data.officialSources?.length)&&<p role="status">{policyStateText(data.state)}</p>}
    {data?.officialSources&&<OfficialSourceList sources={data.officialSources} onSelect={onSelectSource}/>}
    {data?.state==='READY'&&<>
      <p className="rs-calm-policy-note">{data.yearCode}학년도에 연결된 자료입니다. 개별 자료의 적용 여부는 원문과 함께 확인해 주세요.</p>
      {!data.documents.length&&<p role="status">{data.total?'목록이 바뀌었어요. 다시 불러오면 처음부터 확인할 수 있어요.':'이 자료 묶음에 확인할 문서가 아직 없어요.'}</p>}
      <div className="rs-calm-policy-documents">
        {data.documents.map(d=>{
          const url=policySourceUrl(d.canonicalUri);const changed=d.version!==d.currentVersion;
          return <article key={d.id} className="rs-calm-policy-document">
            <div className="rs-calm-policy-authority">{d.authorityName}</div>
            <h2>{d.title}</h2>
            <div className="rs-calm-policy-meta">등록 버전 {d.version}{changed?' · 새 등록 버전 있음':d.recordStatus==='VERIFIED'?' · 등록 검토됨':' · 등록 상태 확인 필요'}</div>
            <details><summary>출처와 확인 상태</summary><dl>
              <div><dt>등록 유형</dt><dd>{d.sourceType}</dd></div>
              <div><dt>등록 검토일</dt><dd>{date(d.verifiedAt)}</dd></div>
              <div><dt>등록된 유효기간</dt><dd>{date(d.effectiveFrom)} ~ {date(d.effectiveUntil)}</dd></div>
              <div><dt>첨부 확인</dt><dd>별도 확인 상태 미기록</dd></div>
            </dl><p>‘등록 검토됨’은 ERP 등록 상태입니다. 원문의 최신성이나 올해 학교 의무를 확정한 표시는 아닙니다.</p>
              {url?<a href={url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">원문 열기 ↗</a>:<span>원문 주소 확인 필요</span>}
            </details>
          </article>;
        })}
      </div>
      {data.total>POLICY_PAGE_SIZE&&<nav className="rs-calm-policy-pages" aria-label="자료 페이지">
        <button className="rs-calm-link" disabled={!offset} onClick={()=>setOffset(Math.max(0,offset-POLICY_PAGE_SIZE))}>이전</button>
        <span>{Math.min(offset+1,data.total)}–{Math.min(offset+data.documents.length,data.total)} / {data.total}</span>
        <button className="rs-calm-link" disabled={data.nextOffset===null} onClick={()=>data.nextOffset!==null&&setOffset(data.nextOffset)}>다음 자료</button>
      </nav>}
      <p className="rs-calm-policy-footnote">등록 자료 조회: {date(data.readAt)} · 새 공고를 검색한 결과는 아닙니다.</p>
    </>}
    {!loading&&<button className="rs-calm-link" onClick={onLegacy}>기존 지침 화면에서 이어가기 ↗</button>}
  </section>;
}
